/**
 * DataFiguresTab - Visual gallery and datasets
 * 
 * All figures and datasets include source links with link-out icons.
 * CRITICAL: Never show fallback sources - only display actual linked sources.
 */

import React, { useMemo, useState, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { DataFiguresView, CIG, TakeawaysView, Source } from '../../../lib/cockpit/schemas'
import { BlockRenderer } from '../blocks'
import OutboundLink from '../../shared/OutboundLink'
import { colorizeNumericContent } from '../../../lib/colorizeNumericText'
import { buildTextFragmentUrl } from '../../../lib/cockpit/textFragmentUrl'

interface DataFiguresTabProps {
  view: DataFiguresView
  takeawaysView?: TakeawaysView
  cig: CIG
  viewMode?: 'mobile' | 'tablet' | 'desktop'
}

// Derive a meaningful subtitle from figure type and title
function deriveSubtitle(type: string, _title: string): string {
  const normalizedType = type?.toLowerCase().replace('_', ' ') || 'chart'
  
  // Generate contextual subtitles based on chart type
  if (normalizedType.includes('line')) {
    return 'Tracking change over time'
  } else if (normalizedType.includes('bar')) {
    return 'Comparing values across categories'
  } else if (normalizedType.includes('pie') || normalizedType.includes('donut')) {
    return 'Distribution of proportions'
  } else if (normalizedType.includes('area')) {
    return 'Cumulative trends over time'
  } else if (normalizedType.includes('scatter')) {
    return 'Relationship between variables'
  } else if (normalizedType.includes('gauge')) {
    return 'Current position relative to target'
  } else if (normalizedType.includes('heatmap')) {
    return 'Intensity patterns across dimensions'
  }
  
  return 'Visual representation of the data'
}

/** Extract source_ids from a stats/KPI block data object */
function extractBlockSourceIds(data: any): string[] {
  if (!data) return []
  const ids: string[] = []
  if (Array.isArray(data.source_ids)) ids.push(...data.source_ids)
  const items = data.stats || data.metrics || data.kpis || []
  for (const item of items) {
    if (item?.anchor?.source_id) ids.push(item.anchor.source_id)
    if (Array.isArray(item?.source_ids)) ids.push(...item.source_ids)
  }
  return [...new Set(ids)]
}

function DataFiguresTab({ view, takeawaysView, cig, viewMode = 'desktop' }: DataFiguresTabProps) {
  const [activeSection, setActiveSection] = useState<'figures' | 'datasets' | 'dictionary' | 'scenarios'>('figures')
  const isMobile = viewMode === 'mobile'
  
  // Build sources lookup map with derived numbers, indexing by all known ID aliases
  const sources = useMemo(() => cig.sources || [], [cig.sources])
  type EnrichedSource = Source & { derivedNumber: number }
  const sourcesById = useMemo(() => {
    const map = new Map<string, EnrichedSource>()
    sources.forEach((source: Source, idx: number) => {
      const enriched: EnrichedSource = { ...source, derivedNumber: idx + 1 }
      if (source.id) map.set(source.id, enriched)
      const raw = source as any
      if (raw.source_id && raw.source_id !== source.id) map.set(raw.source_id, enriched)
      if (raw.card_id && raw.card_id !== source.id) map.set(raw.card_id, enriched)
    })
    return map
  }, [sources])
  
  type ResolvedSource = { url?: string; title: string; number: number }

  const GENERIC_SOURCE_RE = /^deep\s*research\s*(report|source)?$|^supplemental\s*source$/i

  const getSourceLinks = useCallback((sourceIds?: string[]): ResolvedSource[] => {
    if (!sourceIds || sourceIds.length === 0) return []
    const seen = new Set<string>()
    const resolved: ResolvedSource[] = []
    for (const id of sourceIds) {
      const s = sourcesById.get(id)
      if (!s || seen.has(s.id)) continue
      if (GENERIC_SOURCE_RE.test(s.title || '')) continue
      seen.add(s.id)
      const anchorText = s.anchor_text || s.excerpts?.[0]?.text
      const fragmentUrl = buildTextFragmentUrl(s.url, anchorText)
      resolved.push({ url: fragmentUrl, title: s.title, number: s.derivedNumber })
    }
    return resolved
  }, [sourcesById])

  // Keyword-based source matching for figures without resolved source_ids
  const matchSourcesByKeyword = useCallback((figureTitle: string, figureCaption?: string): ResolvedSource[] => {
    if (sources.length === 0) return []
    const textParts = [figureTitle || '', figureCaption || ''].join(' ').toLowerCase()
    const words = new Set(textParts.split(/\s+/).filter(w => w.length > 3))
    if (words.size === 0) return []

    const scored: Array<{ score: number; source: any; idx: number }> = []
    sources.forEach((s: any, idx: number) => {
      if (!s.url || GENERIC_SOURCE_RE.test(s.title || '')) return
      const sTitle = (s.title || '').toLowerCase()
      const sSnippet = ((s as any).snippet || (s as any).summary || (s as any).content_summary || '').toLowerCase()
      let hits = 0
      for (const w of words) {
        if (sTitle.includes(w)) hits += 2
        if (sSnippet.includes(w)) hits += 1
      }
      if (hits >= 2) scored.push({ score: hits, source: s, idx })
    })
    scored.sort((a, b) => b.score - a.score)
    return scored.slice(0, 3).map(({ source: s, idx }) => {
      const anchorText = s.anchor_text || s.excerpts?.[0]?.text
      return { url: buildTextFragmentUrl(s.url, anchorText), title: s.title, number: idx + 1 }
    })
  }, [sources])

  // Only show Figures section to end users (Datasets, Data Dictionary, Scenario Assumptions are internal)
  const sections = useMemo(() => ([
    { id: 'figures' as const, label: 'Figures', count: view.figures.length },
  ].filter(s => s.count > 0)), [view.figures.length])

  if (sections.length === 0 && !(takeawaysView && takeawaysView.key_findings.length > 0)) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
        No structured data or figures were detected for this report yet. Add quantitative metrics or explicit sources to populate this tab automatically.
      </div>
    )
  }

  return (
    <div className={`w-full ${isMobile ? 'px-0' : 'max-w-[1600px] mx-auto p-4 md:p-6'}`}>
      {/* Section tabs - hidden when only one section */}
      {sections.length > 1 && (
        <div className={`flex gap-2 mb-6 overflow-x-auto pb-2 ${isMobile ? 'px-3' : ''}`}>
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors
                ${activeSection === section.id
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }
              `}
            >
              {section.label}
              <span className="ml-2 opacity-60">({section.count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Figures Gallery */}
      {activeSection === 'figures' && (
        <>
          {view.figure_notes && view.figure_notes.length > 0 && (
            <div className={isMobile ? 'px-3' : ''}>
              <InfoBanner
                title="Figures pending data"
                items={view.figure_notes}
              />
            </div>
          )}

          {view.figures.length === 0 ? (
            <div className={isMobile ? 'px-3' : ''}>
              <EmptyPanel
                title="Charts will appear once quantitative data arrives"
                description="The orchestrator didn't receive any chart data for this report yet. As soon as the SIB includes series values, you'll see interactive charts here."
              />
            </div>
          ) : (
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'gap-6 md:grid-cols-2'}`}>
              {view.figures.map((figure: any) => {
                const figureSourceIds = figure.block?.data?.sources || figure.block?.data?.source_ids || figure.data_source_ids
                let resolvedSources = getSourceLinks(figureSourceIds)
                // Fallback: keyword-match against all CIG sources
                if (resolvedSources.length === 0) {
                  resolvedSources = matchSourcesByKeyword(figure.title, figure.caption)
                }
                const subtitle = figure.caption !== figure.title 
                  ? figure.caption 
                  : deriveSubtitle(figure.type, figure.title)
                
                return (
                  <div
                    key={figure.id}
                    className={`bg-white dark:bg-gray-800 overflow-hidden ${
                      isMobile 
                        ? 'mx-2 rounded-xl border border-gray-200 dark:border-gray-700' 
                        : 'rounded-xl border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {/* Header with title and subtitle */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {figure.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {subtitle}
                      </p>
                    </div>
                    
                    {/* Chart content - full width on mobile */}
                    <div className={`w-full ${isMobile ? 'p-1' : 'p-4'}`}>
                      <div className="w-full">
                        <BlockRenderer
                          block={figure.block}
                          showChrome={false}
                          compact={isMobile}
                        />
                      </div>
                    </div>
                    
                    {/* Source attribution */}
                    <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700/50 flex items-center justify-center gap-2 flex-wrap">
                      {resolvedSources.length > 0 ? (
                        <>
                          {resolvedSources.map((source, idx) =>
                            source.url ? (
                              <a
                                key={idx}
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                title={source.title}
                              >
                                <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none">
                                  <path d="M7 13l6-6m0 0h-4m4 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                [{source.number}]
                              </a>
                            ) : (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded-md text-xs text-gray-400 dark:text-gray-500"
                                title={source.title}
                              >
                                [{source.number}]
                              </span>
                            )
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                          See Sources &amp; Methods tab
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* SIB Data Blocks (Stats, KPIs) — polished stat cards */}
      {activeSection === 'figures' && view.sib_blocks && view.sib_blocks.length > 0 && (
        <div className={`mt-8 space-y-6 ${isMobile ? 'px-2' : ''}`}>
          {view.sib_blocks.map((block: any) => {
            const d = block.data || {}
            const stats: any[] = d.stats || d.kpis || d.metrics || []
            const blockSourceIds = extractBlockSourceIds(d)
            const resolvedSources = getSourceLinks(blockSourceIds)

            if (stats.length === 0) return null

            return (
              <div
                key={block.id}
                className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 shadow-sm"
              >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/50">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">{block.title}</h3>
                </div>

                {/* Stat cards grid */}
                <div className={`p-5 grid gap-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {stats.map((stat: any, si: number) => {
                    const val = stat.value ?? stat.amount ?? '—'
                    const label = stat.label || stat.name || stat.title || stat.metric || `Metric ${si + 1}`
                    const context = stat.context || stat.description || stat.notes || ''
                    const status = stat.status || ''
                    const trend = stat.trend
                    const statusBorder = status === 'at_risk' ? 'border-l-amber-400' : status === 'off_track' ? 'border-l-red-400' : status === 'achieved' ? 'border-l-emerald-400' : 'border-l-transparent'

                    return (
                      <div key={si} className={`p-4 rounded-xl bg-gray-50/80 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/40 border-l-[3px] ${statusBorder}`}>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 leading-snug">{label}</p>
                        <div className="flex items-baseline gap-2">
                          <p className="text-xl font-bold text-gray-900 dark:text-white leading-none">{typeof val === 'number' ? val.toLocaleString() : String(val)}</p>
                          {trend && (
                            <span className={`text-xs font-medium ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-500' : 'text-gray-400'}`}>
                              {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}
                            </span>
                          )}
                        </div>
                        {context && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 leading-snug">{context}</p>}
                      </div>
                    )
                  })}
                </div>

                {/* Source links */}
                {resolvedSources.length > 0 && (
                  <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700/50 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">Sources</span>
                    {resolvedSources.map((source, idx) => (
                      <a
                        key={idx}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title={source.title}
                      >
                        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none">
                          <path d="M7 13l6-6m0 0h-4m4 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {source.title.length > 25 ? source.title.slice(0, 22) + '...' : source.title}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Datasets */}
      {activeSection === 'datasets' && (
        <>
          {view.dataset_notes && view.dataset_notes.length > 0 && (
            <InfoBanner
              title="Datasets omitted"
              items={view.dataset_notes}
            />
          )}
          {view.datasets.length === 0 ? (
            <EmptyPanel
              title="No structured tables yet"
              description="Once the intelligence run emits tables with column and row data, they'll appear here."
            />
          ) : (
            <div className="space-y-6">
              {view.datasets.map((dataset: any) => {
                const datasetSourceIds = dataset.block?.data?.source_ids || dataset.source_ids
                const resolvedSources = getSourceLinks(datasetSourceIds)
                
                return (
                  <div
                    key={dataset.id}
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {dataset.name}
                          </h3>
                          {/* Source links for dataset - only show if actual sources exist */}
                          {resolvedSources.length > 0 && (
                            <div className="flex items-center gap-1.5">
                              {resolvedSources.slice(0, 2).map((source, idx) => (
                                <a
                                  key={idx}
                                  href={source.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                  title={source.title}
                                >
                                  <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none">
                                    <path d="M7 13l6-6m0 0h-4m4 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                  [{source.number}]
                                </a>
                              ))}
                              {resolvedSources.length > 2 && (
                                <span className="text-xs text-gray-400">+{resolvedSources.length - 2}</span>
                              )}
                            </div>
                          )}
                        </div>
                        {dataset.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {dataset.description}
                          </p>
                        )}
                        <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
                          <span>{dataset.row_count} rows</span>
                          <span>{dataset.column_count} columns</span>
                        </div>
                      </div>
                      {dataset.download_formats && (
                        <div className="flex gap-2">
                          {dataset.download_formats.map((format: string) => (
                            <button
                              key={format}
                              className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                              {format.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <BlockRenderer
                        block={dataset.block}
                        showChrome={false}
                        compact
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Data Dictionary */}
      {activeSection === 'dictionary' && view.data_dictionary && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800">
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                  Metric
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                  Definition
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {view.data_dictionary.map((item: any) => (
                <tr key={item.metric_key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 font-mono text-gray-900 dark:text-white">
                    {item.label}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {item.definition}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-500">
                    {item.unit || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Scenario Assumptions */}
      {activeSection === 'scenarios' && view.scenario_assumptions && (
        <div className="space-y-6">
          {view.scenario_assumptions.map((scenario: any) => (
            <div
              key={scenario.state_id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {scenario.state_label}
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                      Metric
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                      Base Value
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                      Scenario Value
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                      Rationale
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {scenario.assumptions.map((assumption: any) => (
                    <tr key={assumption.metric_key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-2 text-gray-900 dark:text-white">
                        {assumption.metric_key}
                      </td>
                      <td className="px-4 py-2 text-gray-600 dark:text-gray-400 font-mono">
                        {assumption.base_value}
                      </td>
                      <td className="px-4 py-2 text-blue-600 dark:text-blue-400 font-mono font-medium">
                        {assumption.state_value}
                      </td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-500 text-xs">
                        {assumption.rationale || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {takeawaysView && takeawaysView.key_findings.length > 0 && (
        <section className="mt-10 space-y-3">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Key Findings</h2>
          <div className="space-y-3">
            {takeawaysView.key_findings.map((finding, index) => (
              <div key={index} className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                <div className="flex items-start gap-3">
                  <span className="text-sm font-semibold text-gray-400">{String(index + 1).padStart(2, '0')}</span>
                  <MarkdownInline text={finding.text} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {takeawaysView && takeawaysView.stakeholder_outcomes && takeawaysView.stakeholder_outcomes.length > 0 && (
        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Stakeholder Outcomes</h2>
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
            {takeawaysView.stakeholder_outcomes.map((outcome, index) => (
              <div key={index} className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{outcome.stakeholder}</p>
                <p className="text-xs uppercase tracking-wide text-gray-400">{outcome.outcome}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  <MarkdownInline text={outcome.explanation} />
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {takeawaysView?.decision_options_block && (
        <section className="mt-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Decision Options</h2>
          <BlockRenderer block={takeawaysView.decision_options_block} showChrome={false} />
        </section>
      )}

      {takeawaysView?.premortem_checklist && takeawaysView.premortem_checklist.length > 0 && (
        <section className="mt-10 space-y-3">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Pre-mortem Checklist</h2>
          {takeawaysView.premortem_checklist.map((item, index) => (
            <div key={index} className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <p className="font-medium text-gray-900 dark:text-white">{item.risk}</p>
              {item.mitigation && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Mitigation: <MarkdownInline text={item.mitigation} />
                </p>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

function MarkdownInline({ text }: { text: string }) {
  if (!text) return null
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href }) => (
          <OutboundLink
            href={href}
            className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:text-blue-500"
          />
        ),
        p: ({ children }) => <>{colorizeNumericContent(children)}</>,
        strong: ({ children }) => <strong className="font-semibold">{colorizeNumericContent(children)}</strong>,
        em: ({ children }) => <em className="italic">{colorizeNumericContent(children)}</em>,
      }}
    >
      {text}
    </ReactMarkdown>
  )
}

function InfoBanner({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50/70 text-amber-900 dark:bg-amber-900/10 dark:border-amber-700/40 dark:text-amber-200 p-4">
      <p className="text-sm font-semibold mb-2">{title}</p>
      <ul className="text-xs space-y-1">
        {items.map((note, index) => (
          <li key={index} className="flex gap-2">
            <span>•</span>
            <span>{note}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function EmptyPanel({ title, description }: { title: string; description: string }) {
  return (
    <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl p-6 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900/40">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
      <p className="text-sm">{description}</p>
    </div>
  )
}

export default DataFiguresTab



